import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  HALO_STATUS,
  HaloStatus,
} from '@savgroup-front-common/core/components/AssistantBricks/DesignationHalo/DesignationHalo.types';
import { SuggestionConfidence } from '@savgroup-front-common/types/CompanyDirectory';

import { adaptLocationState } from './adapters/adaptLocationState';
import { prepareActivitiesSubmitValues } from './adapters/prepareActivitiesSubmitValues';
import { useCompanyProfileSuggestion } from './hooks/useCompanyProfileSuggestion';
import {
  useProposalReveal,
  UseProposalRevealReturnValues,
} from './hooks/useProposalReveal';
import useSavePersonalizedDemoContextCommand from './hooks/useSavePersonalizedDemoContextCommand';
import { ACTIVITY_GROUP, ActivityGroup } from './OnboardingActivities.constant';
import {
  getActivitiesSelection,
  getChangedActivityGroups,
  hasActivitiesProposal,
  isActivitiesSelectionComplete,
  toggleActivityValue,
} from './OnboardingActivities.helpers';
import { ActivitiesSelection } from './OnboardingActivities.types';

interface HandleToggleArgs {
  group: ActivityGroup;
  value: string;
}

interface UseOnboardingActivitiesReturnValues {
  selection: ActivitiesSelection;
  proposedSelection: ActivitiesSelection;
  summary?: string;
  confidence?: SuggestionConfidence;
  companyName?: string;
  sectionRefs: UseProposalRevealReturnValues['sectionRefs'];
  getIsGroupRevealed: UseProposalRevealReturnValues['getIsGroupRevealed'];
  isProfileLoading: boolean;
  hasSuggestionFailed: boolean;
  hasProposal: boolean;
  isUnrelatedActivity: boolean;
  isComplete: boolean;
  changedGroups: ActivityGroup[];
  isSaving: boolean;
  getHaloStatus: (group: ActivityGroup) => HaloStatus;
  handleToggle: (args: HandleToggleArgs) => void;
  handleSubmit: () => Promise<undefined>;
}

export function useOnboardingActivities(): UseOnboardingActivitiesReturnValues {
  const { state } = useLocation();
  const { pickedCompany, companyName } = adaptLocationState(state);
  const {
    proposedProfile,
    isLoading: isProfileLoading,
    hasSuggestionFailed,
    hasPickedCompany,
  } = useCompanyProfileSuggestion({ pickedCompany });
  const { handleSubmitSalesLead, isLoading: isSaving } =
    useSavePersonalizedDemoContextCommand();
  const [editedSelection, setEditedSelection] = useState<
    Partial<ActivitiesSelection>
  >({});

  const { summary, confidence, ...proposedSelection } = proposedProfile;

  const selection = getActivitiesSelection({
    proposedSelection,
    editedSelection,
  });

  const hasProposal = hasActivitiesProposal(proposedSelection);
  const { sectionRefs, activeRevealGroup, getIsGroupRevealed, finishReveal } =
    useProposalReveal({
      proposedSelection,
      hasProposal,
      isProfileLoading,
    });

  function handleToggle({ group, value }: HandleToggleArgs): void {
    finishReveal();

    if (!getIsGroupRevealed(group) && selection[group].includes(value)) {
      return;
    }

    setEditedSelection((previousSelection) => ({
      ...previousSelection,
      [group]: toggleActivityValue({
        values: getActivitiesSelection({
          proposedSelection,
          editedSelection: previousSelection,
        })[group],
        value,
        isMultiple: group === ACTIVITY_GROUP.DOMAINS,
      }),
    }));
  }

  function getHaloStatus(group: ActivityGroup): HaloStatus {
    if (isProfileLoading || group === activeRevealGroup) {
      return HALO_STATUS.WORKING;
    }

    if (!getIsGroupRevealed(group) || proposedSelection[group].length === 0) {
      return HALO_STATUS.IDLE;
    }

    return HALO_STATUS.DONE;
  }

  const handleSubmit = (): Promise<undefined> =>
    handleSubmitSalesLead(prepareActivitiesSubmitValues({ selection }));

  return {
    selection,
    proposedSelection,
    summary,
    confidence,
    companyName,
    sectionRefs,
    getIsGroupRevealed,
    isProfileLoading,
    hasSuggestionFailed,
    hasProposal,
    isUnrelatedActivity:
      hasPickedCompany &&
      !isProfileLoading &&
      !hasSuggestionFailed &&
      !hasProposal,
    isComplete: isActivitiesSelectionComplete(selection),
    changedGroups: getChangedActivityGroups({ proposedSelection, selection }),
    isSaving,
    getHaloStatus,
    handleToggle,
    handleSubmit,
  };
}
